import React from 'react';
import { formatDate } from '../functions';

function TaskCard({ task, onClick, onToggleComplete }) {
    const isCompleted = task.kanban_status === 'Done' || task.status === 'Done' || !!task.completed_date;
    const isOverdue = task.due_date && new Date(task.due_date) < new Date() && !isCompleted;
    
    // Parse assigned employees if it's a JSON string
    let assignedEmployees = [];
    if (task.assigned_employees) {
        try {
            assignedEmployees = typeof task.assigned_employees === 'string' 
                ? JSON.parse(task.assigned_employees) 
                : task.assigned_employees;
        } catch (e) {
            console.error('[TaskCard] Error parsing assigned_employees:', e);
        }
    }

    // Parse students if it's a JSON string
    let students = [];
    if (task.students) {
        try {
            students = typeof task.students === 'string' 
                ? JSON.parse(task.students) 
                : task.students;
        } catch (e) {
            console.error('[TaskCard] Error parsing students:', e);
        }
    }

    const priorityColors = {
        'Low': 'bg-blue-100 text-blue-800',
        'Medium': 'bg-yellow-100 text-yellow-800',
        'High': 'bg-orange-100 text-orange-800',
        'Urgent': 'bg-red-100 text-red-800'
    };

    return (
        <div
            className={`rounded-lg shadow-sm border p-3 mb-2 cursor-pointer transition-shadow ${
                isCompleted
                    ? 'bg-gray-100 border-gray-200 opacity-70'
                    : (isOverdue ? 'border-red-300 bg-red-50 hover:shadow-md' : 'bg-white border-gray-200 hover:shadow-md')
            }`}
            onClick={() => onClick && onClick(task)}
        >
            {/* Header: Complete checkbox + title */}
            <div className="flex items-start gap-2">
                <input
                    type="checkbox"
                    checked={isCompleted}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                        e.stopPropagation();
                        onToggleComplete && onToggleComplete(task, e.target.checked);
                    }}
                    className="mt-1 h-4 w-4"
                    aria-label="Mark complete"
                />
            {/* Task Name */}
                <div className={`font-medium mb-1 truncate ${isCompleted ? 'text-gray-600 line-through' : 'text-gray-900'}`}>
                    {task.task_name || task.notes || 'Untitled Task'}
                </div>
            </div>

            {/* Priority Badge */}
            {task.priority && (
                <div className="mb-2">
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${priorityColors[task.priority] || 'bg-gray-100 text-gray-800'}`}>
                        {task.priority}
                    </span>
                </div>
            )}

            {/* Due Date */}
            {task.due_date && (
                <div className={`text-xs mb-1 ${isOverdue ? 'text-red-600 font-semibold' : (isCompleted ? 'text-gray-400' : 'text-gray-600')}`}>
                    <i className="far fa-calendar-alt mr-1"></i>
                    {formatDate(task.due_date)}
                    {isOverdue && <span className="ml-1">⚠️ Overdue</span>}
                </div>
            )}

            {/* Project */}
            {task.project_name && (
                <div className={`text-xs mb-1 truncate ${isCompleted ? 'text-gray-400' : 'text-gray-500'}`}>
                    <i className="far fa-folder mr-1"></i>
                    {task.project_name}
                </div>
            )}

            {/* Center */}
            {task.centerName && (
                <div className={`text-xs mb-1 truncate ${isCompleted ? 'text-gray-400' : 'text-gray-500'}`}>
                    <i className="fas fa-building mr-1"></i>
                    {task.centerName}
                </div>
            )}

            {/* Assigned Employees */}
            {assignedEmployees.length > 0 && (
                <div className={`text-xs mb-1 ${isCompleted ? 'text-gray-400' : 'text-gray-500'}`}>
                    <i className="fas fa-users mr-1"></i>
                    {task.assignedEmployeeNames && task.assignedEmployeeNames.length > 0 ? (
                        <span className="truncate block" title={task.assignedEmployeeNames.join(', ')}>
                            {task.assignedEmployeeNames.join(', ')}
                        </span>
                    ) : (
                        <span>{assignedEmployees.length} employee{assignedEmployees.length !== 1 ? 's' : ''} assigned</span>
                    )}
                </div>
            )}

            {/* Students */}
            {students.length > 0 && (
                <div className={`text-xs mb-1 ${isCompleted ? 'text-gray-400' : 'text-gray-500'}`}>
                    <i className="fas fa-user-graduate mr-1"></i>
                    {task.studentNames && task.studentNames.length > 0 ? (
                        <span className="truncate block" title={task.studentNames.join(', ')}>
                            {task.studentNames.join(', ')}
                        </span>
                    ) : (
                        <span>{students.length} student{students.length !== 1 ? 's' : ''} linked</span>
                    )}
                </div>
            )}

            {/* Notes Preview */}
            {task.notes && (
                <div className={`text-xs mt-2 line-clamp-2 ${isCompleted ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span className="font-semibold">Notes:</span> {task.notes}
                </div>
            )}

            {/* Follow-up Notes */}
            {task.followup_notes && task.followup_notes.trim() && (
                <div className={`text-xs mt-1 line-clamp-2 ${isCompleted ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span className="font-semibold">Follow-up:</span> {task.followup_notes}
                </div>
            )}

            {/* User/Student */}
            <div className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-200 truncate">
                <i className="fas fa-user mr-1"></i>
                {task.userName || task.studentName || 'Unknown'}
            </div>
        </div>
    );
}

export default TaskCard;

